import { ApiError } from './client';

async function send<T = unknown>(
  path: string,
  method: 'POST' | 'PUT' | 'DELETE',
  body?: unknown,
): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  let data: T | { error?: string };
  try {
    data = await res.json();
  } catch {
    data = {} as T;
  }
  if (!res.ok) {
    const detail = (data as { error?: string }).error ?? `${res.status} ${res.statusText}`;
    throw new ApiError(detail, res.status);
  }
  return data as T;
}

export type SpellCheckFix = {
  original: string;
  corrected: string;
  reason: string;
};

export type SpellCheckResult = {
  bodyHash: string;
  hasChanges: boolean;
  originalHtml: string;
  correctedHtml: string;
  fixes: SpellCheckFix[];
  cached?: boolean;
};

export const contentApi = {
  // publishAt: ISO-8601 string. Omit/empty for immediate publish.
  approve: (id: number) =>
    send<{ ok: boolean }>(`/api/content/submissions/${id}/approve`, 'POST', {}),
  reject: (id: number, reason?: string) =>
    send<{ ok: boolean }>(`/api/content/submissions/${id}/reject`, 'POST', { reason }),
  hold: (id: number) =>
    send<{ ok: boolean; held_by_name: string | null }>(`/api/content/submissions/${id}/hold`, 'POST', {}),
  unhold: (id: number) =>
    send<{ ok: boolean; held_by_name: string | null }>(`/api/content/submissions/${id}/unhold`, 'POST', {}),

  // Ad-hoc spellcheck for the submitter form (no submission row yet).
  spellcheck: (html: string) =>
    send<SpellCheckResult>('/api/content/spellcheck', 'POST', { html }),

  // Per-submission spellcheck for the marketer approval drawer. Cached by
  // body hash so reopening the drawer doesn't re-call the LLM.
  spellcheckSubmission: (id: number, field: 'news_content' | 'blog_content') =>
    send<SpellCheckResult>(
      `/api/content/submissions/${id}/spellcheck`,
      'POST',
      { field },
    ),

  // Apply the cached corrected HTML back to the submission body. expectedHash
  // is the bodyHash from the SpellCheckResult — the server rejects with 409
  // if the underlying body has changed since the check ran.
  applySpellcheck: (
    id: number,
    field: 'news_content' | 'blog_content',
    expectedHash: string,
  ) =>
    send<{ ok: boolean; appliedFixes: number }>(
      `/api/content/submissions/${id}/spellcheck/apply`,
      'POST',
      { field, expectedHash },
    ),

  // Save marketer edits to a submission body (raw stored HTML). Pending
  // submissions only; drops any cached spellcheck for that field server-side.
  saveBody: (id: number, field: 'news_content' | 'blog_content', html: string) =>
    send<{ ok: boolean; html: string }>(
      `/api/content/submissions/${id}/body`,
      'PUT',
      { field, html },
    ),

  // SEO + alt-text generation/save for the approval drawer.
  generateSeoMetadata: (id: number) =>
    send<{ seo: YoastFields | null; alts: ImageMeta[]; summary: string | null; showSummary: boolean | null }>(
      `/api/content/submissions/${id}/seo-metadata/generate`,
      'POST',
      {},
    ),
  saveSeoMetadata: (
    id: number,
    payload: { seo?: YoastFields; alts?: ImageMeta[]; summary?: string; showSummary?: boolean },
  ) =>
    send<{ ok: boolean }>(
      `/api/content/submissions/${id}/seo-metadata`,
      'PUT',
      payload,
    ),
  // Save the (admin-edited) blog author name. Gated in the UI behind a
  // confirm dialog.
  saveAuthor: (id: number, author: string) =>
    send<{ ok: boolean }>(`/api/content/submissions/${id}/author`, 'PUT', { author }),

  // Put a failed publish back on the publisher's queue. Server rejects with
  // 409 unless the submission is actually in publish_failed.
  retryPublish: (id: number) =>
    send<{ ok: boolean }>(`/api/content/submissions/${id}/retry-publish`, 'POST', {}),

  // Clear a failed publish out of the queue without deleting it. Reversible:
  // retryPublish un-dismisses.
  dismissFailure: (id: number) =>
    send<{ ok: boolean }>(`/api/content/submissions/${id}/dismiss-failure`, 'POST', {}),

  // Record that marketing has been told this franchisee needs their blog
  // options set up (suppresses the "first blog" notice for the franchisee).
  ackBlogSetup: (id: number) =>
    send<{ ok: boolean }>(`/api/content/submissions/${id}/ack-blog-setup`, 'POST', {}),
};

export type YoastFields = {
  seoTitle: string;
  metaDesc: string;
  slug: string;
};

export type ImageMeta = {
  alt: string;
  title: string;
};
